import { useState, MouseEvent } from "react";
import { useTranslation } from "react-i18next";
import { FileText, X } from "lucide-react";

import { CreativeLightbox } from "@/components/chat/CreativeLightbox";
import { CreativeMedia, isVideoCreativeLike } from "@/components/chat/CreativeMedia";

export interface AttachmentTileItem {
  src: string;
  mimeType?: string;
  label?: string;
}

interface AttachmentTilesProps {
  items: Array<AttachmentTileItem>;
  /**
   * Singular tile prefix. Default `"sent-attachment"` preserves the QS
   * surface: `sent-attachment-${index}`, `sent-attachments-scroller` (prefix + `s-scroller`),
   * `sent-attachment-preview-overlay`.
   */
  testIdPrefix?: string;
  /** Thread rail sits start-aligned; QS bubbles stay end-aligned. */
  align?: "start" | "end";
  /** Optional per-tile remove. Omitted on QS sent-attachment tiles. */
  onRemoveAtIndex?: (index: number) => void;
}

/** `m:ss` for a clip duration, e.g. 42.6 -> "0:43", 95 -> "1:35". Returns null for a
 *  duration a browser never really parsed (NaN, Infinity, zero, negative), so the chip
 *  simply does not render rather than showing "0:00" or "NaN:aN". */
export function formatClipDuration(durationSec: number): string | null {
  if (!Number.isFinite(durationSec) || durationSec <= 0) {
    return null;
  }
  const total = Math.round(durationSec);
  const minutes = Math.floor(total / 60);
  const seconds = total % 60;
  return `${minutes}:${String(seconds).padStart(2, "0")}`;
}

/**
 * Shared attachment thumbnails (a wrapping row) + lightbox. Owns zoom preview.
 * Duration chips are keyed by label+src+index so a reordered list never
 * shows one clip's length under another.
 */
export function AttachmentTiles(props: AttachmentTilesProps) {
  const {
    items,
    testIdPrefix = "sent-attachment",
    align = "end",
    onRemoveAtIndex,
  } = props;

  const { t } = useTranslation("app");
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);
  const [durationsByKey, setDurationsByKey] = useState<Record<string, number>>({});

  const lightboxCreatives = items.flatMap((item) =>
    item.src ? [{ src: item.src, mimeType: item.mimeType }] : [],
  );

  function handleOpenLightbox(itemIndex: number) {
    setLightboxIndex(items.slice(0, itemIndex).filter((it) => it.src).length);
  }

  function handleCloseLightbox() {
    setLightboxIndex(null);
  }

  if (items.length === 0) {
    return null;
  }

  return (
    <>
      {/* The outer box aligns the set; the inner one wraps it, so every tile
          stays on screen however many there are (B-UI-MISC U8). */}
      <div
        className={
          align === "start"
            ? "flex min-w-0 flex-1 justify-start"
            : "flex min-w-0 flex-1 justify-end"
        }
      >
        <div
          data-testid={`${testIdPrefix}s-scroller`}
          className={
            align === "start"
              ? "flex max-w-full flex-wrap items-end justify-start gap-2"
              : "flex max-w-full flex-wrap items-end justify-end gap-2"
          }
        >
          {items.map((item, index) => {
            const tileKey = `${item.label ?? ""}-${item.src}-${index}`;
            const isVideoTile = isVideoCreativeLike({
              mimeType: item.mimeType,
              src: item.src,
            });
            const reportedDuration = durationsByKey[tileKey];
            const durationLabel =
              reportedDuration === undefined ? null : formatClipDuration(reportedDuration);

            const tile = item.src ? (
              <button
                type="button"
                onClick={() => handleOpenLightbox(index)}
                data-testid={`${testIdPrefix}-${index}`}
                className="relative inline-flex h-32 w-auto min-w-[4rem] shrink-0 cursor-zoom-in items-center justify-center overflow-hidden rounded-[12px] border border-border transition-opacity hover:opacity-90"
              >
                {/* The tile takes the media's own shape. Media too narrow for
                    the tile's minimum width (a full-page screenshot) covers it
                    from its top, as the exposure strip draws a thumbnail,
                    never a sliver (B-UI-MISC U9). */}
                <CreativeMedia
                  src={item.src}
                  mimeType={item.mimeType}
                  alt={item.label}
                  className="h-full w-full object-cover object-top"
                  playOnHover={isVideoTile ? true : undefined}
                  onMeta={
                    isVideoTile
                      ? (meta) =>
                          setDurationsByKey((cur) =>
                            cur[tileKey] === meta.durationSec
                              ? cur
                              : { ...cur, [tileKey]: meta.durationSec },
                          )
                      : undefined
                  }
                />
                {durationLabel ? (
                  <span
                    data-testid={`${testIdPrefix}-${index}-duration`}
                    className="pointer-events-none absolute bottom-1 left-1 rounded-full bg-black/60 px-1.5 py-0.5 text-[10px] font-medium leading-none text-white"
                  >
                    {durationLabel}
                  </span>
                ) : null}
              </button>
            ) : (
              <div
                aria-label={item.label}
                data-testid={`${testIdPrefix}-${index}`}
                className="flex h-32 w-40 shrink-0 items-center justify-center rounded-[12px] border border-border bg-muted text-muted-foreground"
              >
                <FileText className="h-7 w-7" />
              </div>
            );

            if (!onRemoveAtIndex) {
              return (
                <span key={tileKey} className="contents">
                  {tile}
                </span>
              );
            }

            return (
              <div
                key={tileKey}
                className="group relative shrink-0"
                data-testid={`${testIdPrefix}-tile-${index}`}
              >
                {tile}
                <button
                  type="button"
                  className="absolute -right-1.5 -top-1.5 rounded-full bg-destructive p-0.5 text-destructive-foreground opacity-0 transition-opacity group-hover:opacity-100"
                  data-testid={`${testIdPrefix}-remove-${index}`}
                  aria-label={t("chatChrome.removeAttachment", {
                    label: item.label ?? t("chatChrome.assetFallback"),
                  })}
                  onClick={(event: MouseEvent<HTMLButtonElement>) => {
                    event.stopPropagation();
                    onRemoveAtIndex(index);
                  }}
                >
                  <X className="h-3 w-3" aria-hidden />
                </button>
              </div>
            );
          })}
        </div>
      </div>

      {lightboxIndex !== null && lightboxCreatives.length > 0 && (
        <CreativeLightbox
          creatives={lightboxCreatives}
          index={lightboxIndex}
          onIndex={setLightboxIndex}
          onClose={handleCloseLightbox}
          dataTestId={`${testIdPrefix}-preview-overlay`}
        />
      )}
    </>
  );
}
